import EditorContent from "../common/EditorContent";
import { appendVariantLineItems, sortedVariantLineItems, removedVariantIds, variantLineItemIdentity, variantLineItemsMap } from "../../helper_functions/variantLineItems";
import type { Bundle, ChildBundleHorseVariant, IHorseVariant, LineItemVariant } from "../../api_utils/types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionList,
  BlockStack,
  Box,
  Button,
  Card,
  EmptyState,
  Icon,
  IndexTable,
  InlineStack,
  Layout,
  Page,
  PageActions,
  Popover,
  SkeletonBodyText,
  SkeletonPage,
  Text,
  TextField,
} from "@shopify/polaris";
import { MenuHorizontalIcon, EditIcon, XIcon } from "@shopify/polaris-icons";
import { isEqual, getIdFromPath, extractMessageFromError, cloneNestedObjects } from "../../helper_functions/utils";
import AddVariantDialog from "../common/AddVariantDialog";
import { SelectVariantDialog } from "../common/SelectVariantDialog";
import DeleteConfirmationDialog from "../common/DeleteConfirmationDialog";
import HorseVariant from "../common/HorseVariant/HorseVariant";
import SettingToggle from "../common/SettingToggle";
import {
  deleteBundle,
  getBundle,
  updateBundle,
} from "../../api_utils/requests";
import { ErrorBanner } from "../common/ErrorBanner";
import { Footer } from "../common/Footer";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import SaveBar from "../common/SaveBar";
import { useNavigate } from "react-router-dom";

const bundlesHelpPageUrl = "https://horse-inventory.notion.site/Bundles-88ac584381a445de890d4f68870d5920";
const helpUrl =
  "https://horse-inventory.notion.site/Bundles-88ac584381a445de890d4f68870d5920?pvs=4#30886ee8a00f4c35b26620d83910eee4";

type ChildBundleHorseVariants = Record<number, ChildBundleHorseVariant & { sortIndex: number }>;

const columnNames: NonEmptyArray<IndexTableHeading> = [{ title: "Variant" }, { title: "Quantity" }, { title: "" }];

const emptyState = (
  <EmptyState heading="No variants added" image="">
    <p>You have not added any variants to your bundle yet.</p>
  </EmptyState>
);

function ShowBundle(): React.JSX.Element {
  const bundleId = useMemo(() => getIdFromPath(), []);

  const [bundle, setBundle] = useState<Bundle>();
  const originalBundle = useRef<Bundle>();

  const [bundleHorseVariants, setBundleHorseVariants] = useState<ChildBundleHorseVariants>(
    {} as ChildBundleHorseVariants,
  );
  const originalBundleHorseVariants = useRef<ChildBundleHorseVariants>({} as ChildBundleHorseVariants);

  const [bundleHorseVariant, setBundleHorseVariant] = useState<IHorseVariant>();
  const originalBundleHorseVariant = useRef<IHorseVariant>();

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    shopify.loading(loading);
  }, [loading]);
  const [addVariantModalActive, setAddVariantModalActive] = useState(false);
  const [popoverActive, setPopoverActive] = useState(false);
  const [selectVariantModalActive, setSelectVariantModalActive] = useState(false);
  const [deletionModalActive, setDeletionModalActive] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const isDirty = !isEqual(bundle, originalBundle.current) || !isEqual(bundleHorseVariants, originalBundleHorseVariants.current);

  const togglePopoverActive = useCallback(() => {
    setPopoverActive((currentPopoverActive) => !currentPopoverActive);
  }, []);

  useEffect(() => {
    getBundle(bundleId)
      .then(({ bundle: newBundle, bundleHorseVariants: newBundleHorseVariants }) => {
        setBundle(newBundle);
        originalBundle.current = { ...newBundle };

        setBundleHorseVariant(newBundle.horseVariant);
        originalBundleHorseVariant.current = { ...newBundle.horseVariant };

        const formattedHorseVariants = variantLineItemsMap(newBundleHorseVariants);
        setBundleHorseVariants(formattedHorseVariants);
        originalBundleHorseVariants.current = cloneNestedObjects(formattedHorseVariants);

        setLoading(false);
      })
      .catch((error: unknown) => {
        console.error("Error fetching data:", error);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBundleChange = useCallback((value: string | boolean, key: string) => {
    setBundle((prevBundle) => {
      return { ...prevBundle, [key]: value };
    });
  }, []);

  const handleHorseVariantUpdate = (horseVariant: IHorseVariant): void => {
    setBundleHorseVariant(horseVariant);
    setBundle({ ...bundle, horse_variant_id: horseVariant.id });
  };

  const addVariants = (horseVariants: LineItemVariant[]): void => {
    const addedLines = variantLineItemsMap(horseVariants.map((horseVariant) =>
      originalBundleHorseVariants.current[horseVariant.id] || {
        ...horseVariant, id: null, horse_variant_id: horseVariant.id, bundle_id: bundleId, quantity: 1,
      },
    ));
    setBundleHorseVariants((lineItems) => appendVariantLineItems(lineItems, addedLines));
  };

  const handleHorseVariantsQuantityChange = useCallback(
    (variantId: number, newQuantity: string, key: string) => {
      const newBundleHorseVariants = JSON.parse(JSON.stringify(bundleHorseVariants)) as ChildBundleHorseVariants;
      const cleanQuantity = newQuantity?.replace(/[^\d.-]/g, "");
      newBundleHorseVariants[variantId][key] = cleanQuantity;
      setBundleHorseVariants(newBundleHorseVariants);
    },
    [bundleHorseVariants],
  );

  const handleSaveBundle = useCallback(() => {
    setLoading(true);

    const payload = {
      bundle: {
        ...bundle,
        bundle_horse_variants_attributes: sortedVariantLineItems(bundleHorseVariants).map((lineItem) => ({
          ...variantLineItemIdentity(lineItem), quantity: lineItem.quantity,
        })),
        removed_horse_variant_ids: removedVariantIds(originalBundleHorseVariants.current, bundleHorseVariants),
      },
    };
    return updateBundle(bundleId, payload)
      .then(({ bundle: updatedBundle, bundleHorseVariants: savedVariants }) => {
        shopify.toast.show("Save successful");

        setBundle(updatedBundle);
        originalBundle.current = { ...updatedBundle };

        setBundleHorseVariant(updatedBundle.horseVariant);
        originalBundleHorseVariant.current = { ...updatedBundle.horseVariant };

        const savedLineItems = variantLineItemsMap(savedVariants);
        setBundleHorseVariants(savedLineItems);
        originalBundleHorseVariants.current = cloneNestedObjects(savedLineItems);
        setErrorMessage("");
      })
      .catch((err: unknown) => {
        Rollbar.error(err, { bundleId, payload });
        const newErrorMessage = extractMessageFromError(err);
        setErrorMessage(newErrorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [bundle, bundleHorseVariants, bundleId]);

  const removeVariants = (horseVariantIds: number[]): void => {
    const removedIds = new Set(horseVariantIds);
    setBundleHorseVariants((lineItems) => Object.fromEntries(
      Object.entries(lineItems).filter(([id]) => !removedIds.has(Number(id))),
    ));
  };

  const handleDiscardAction = useCallback(() => {
    setBundle(originalBundle.current);
    setBundleHorseVariant(originalBundleHorseVariant.current);
    setBundleHorseVariants(cloneNestedObjects(originalBundleHorseVariants.current));
  }, [originalBundle, originalBundleHorseVariant, originalBundleHorseVariants]);

  const activator = (
    <Button accessibilityLabel="Menu" disclosure icon={MenuHorizontalIcon} onClick={togglePopoverActive} />
  );

  const pageSkeleton = (
    <SkeletonPage>
      <Layout>
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            <Card>
              <SkeletonBodyText lines={2} />
            </Card>
            <Card>
              <SkeletonBodyText lines={5} />
            </Card>
          </BlockStack>
        </Layout.Section>
        <Layout.Section>
          <Card>
            <SkeletonBodyText lines={10} />
          </Card>
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  );

  const navigate = useNavigate();

  const pageMarkup = (
    <Page
      backAction={{
        content: "Bundles",
        onAction: async () => {
          await shopify.saveBar.leaveConfirmation();

          navigate("/bundles");
        },
      }}
      title={bundle?.name}
    >
      <SaveBar disabled={loading} handleDiscard={handleDiscardAction} handleSave={handleSaveBundle} isDirty={isDirty} />

      <SelectVariantDialog
        active={selectVariantModalActive ? !loading : null}
        handleDiscard={() => {
          setBundleHorseVariant({ ...originalBundleHorseVariant.current });
        }}
        selected={bundleHorseVariant}
        setActive={setSelectVariantModalActive}
        updateSelection={handleHorseVariantUpdate}
      />
      <Layout>
        {errorMessage ? (
          <Layout.Section variant="fullWidth">
            <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
          </Layout.Section>
        ) : null}
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            <Card>
              <TextField autoComplete="off" id="name" label="Name" onChange={handleBundleChange} value={bundle?.name} />
            </Card>
            <Card>
              <Text as="h6" variant="headingSm">
                Bundle parent variant
              </Text>
              <Box paddingBlock="200">
                <Text as="h6" variant="bodySm">
                  The variant that the customer buys
                </Text>
              </Box>
              <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center">
                  {bundleHorseVariant ? <HorseVariant variant={bundleHorseVariant} /> : null}
                  <Popover
                    activator={activator}
                    active={popoverActive}
                    autofocusTarget="first-node"
                    onClose={togglePopoverActive}
                  >
                    <ActionList
                      actionRole="menuitem"
                      items={[
                        {
                          content: "Edit parent bundle variant",
                          icon: EditIcon,
                          onAction: (): void => {
                            setSelectVariantModalActive(true);
                          },
                        },
                      ]}
                    />
                  </Popover>
                </InlineStack>
              </BlockStack>
            </Card>
            <Card>
              <SettingToggle
                description="Automatically adjust the inventory of the parent according to the available quantities of its children."
                enabled={bundle?.parent_inventory_tracks_children}
                handleToggle={() => {
                  handleBundleChange(!bundle?.parent_inventory_tracks_children, "parent_inventory_tracks_children");
                }}
                helpLink={helpUrl}
                title="Track parent quantity"
              />
            </Card>
          </BlockStack>
        </Layout.Section>
        <Layout.Section>
          <Card padding="0">
            <Box padding="400">
              <Text as="h6" variant="headingSm">
                Add child variants to the bundle
              </Text>
              <Box paddingBlock="200">
                <Text as="h6" variant="bodySm">
                  These variants are inside the bundle
                </Text>
              </Box>
            </Box>
            <BlockStack gap="200">
              <InlineStack align="center">
                <AddVariantDialog
                  active={addVariantModalActive}
                  disabled={loading}
                  excludableHorseVariantIds={[bundle?.horse_variant_id]}
                  onAdd={addVariants}
                  onRemove={removeVariants}
                  primary
                  selectedVariantIds={Object.values(bundleHorseVariants).map(({ horse_variant_id }) => horse_variant_id)}
                  setActive={setAddVariantModalActive}
                  setErrorMessage={setErrorMessage}
                />
              </InlineStack>
              <IndexTable
                emptyState={emptyState}
                headings={columnNames}
                itemCount={Object.keys(bundleHorseVariants).length}
                selectable={false}
              >
                {sortedVariantLineItems(bundleHorseVariants).map((variant, index) => (
                  <IndexTable.Row id={variant.horse_variant_id.toString()} key={variant.horse_variant_id} position={index}>
                    <IndexTable.Cell>
                      <HorseVariant key={variant.horse_variant_id} variant={variant} />
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <div className="w120">
                        <TextField
                          autoComplete="off"
                          id="quantity"
                          label="Quantity"
                          labelHidden
                          min={1}
                          name="Quantity"
                          onChange={(value: string, key: string): void => {
                            handleHorseVariantsQuantityChange(variant.horse_variant_id, value, key);
                          }}
                          type="number"
                          value={String(variant.quantity ?? 0)}
                        />
                      </div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Button
                        icon={<Icon source={XIcon} />}
                        onClick={(): void => {
                          removeVariants([variant.horse_variant_id]);
                        }}
                        variant="plain"
                      />
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
      <PageActions
        secondaryActions={[
          {
            content: "Delete",
            destructive: true,
            onAction: () => {
              setDeletionModalActive(true);
            },
          },
        ]}
      />
      <DeleteConfirmationDialog
        active={deletionModalActive}
        content="Are you sure you want to delete this bundle? This action cannot be reversed."
        deleteItem={deleteBundle}
        gotoPath="/bundles"
        invalidateQueries={{
          queryKey: ["bundles", { page: 1 }],
        }}
        itemId={bundleId}
        setActive={setDeletionModalActive}
        title="Delete bundle"
        toastMessage="Bundle deleted"
      />
      <Footer pageTitle="bundles" url={bundlesHelpPageUrl} />
    </Page>
  );

  return !bundle ? pageSkeleton : <EditorContent disabled={loading}>{pageMarkup}</EditorContent>;
}

export default ShowBundle;
